import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Check, Infinity as InfinityIcon, RefreshCw,
  Search, Sparkles, X,
} from 'lucide-react';
import {
  ProAccessAction, ProAccessUser, adjustProAccess, fetchProAccess,
} from '../services/api';

const MicroLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink-400">
    {children}
  </span>
);

type Standing = 'unlimited' | 'active' | 'lapsed' | 'never';

const standingOf = (user: ProAccessUser): Standing => {
  if (user.is_pro_active && user.pro_never_expires) return 'unlimited';
  if (user.is_pro_active) return 'active';
  if (user.is_pro) return 'lapsed';
  return 'never';
};

const STANDING: Record<Standing, { label: string; chip: string; dot: string }> = {
  unlimited: {
    label: 'Unlimited',
    chip: 'bg-violet-50 border-violet-200 text-violet-700', dot: 'bg-violet-500',
  },
  active: {
    label: 'Active',
    chip: 'bg-emerald-50 border-emerald-200 text-emerald-700', dot: 'bg-emerald-500',
  },
  lapsed: {
    label: 'Lapsed',
    chip: 'bg-amber-50 border-amber-200 text-amber-700', dot: 'bg-amber-500',
  },
  never: {
    label: 'Never bought',
    chip: 'bg-ink-50 border-ink-200 text-ink-500', dot: 'bg-ink-300',
  },
};

const formatDate = (iso: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

/** "3 days left", "today", "lapsed 2 days ago" - the thing an admin scans for. */
const countdown = (user: ProAccessUser) => {
  if (user.pro_never_expires && user.is_pro) return 'No expiry';
  if (!user.is_pro) return '--';
  const days = user.pro_days_remaining;
  if (days === null) return '--';
  if (!user.is_pro_active) return 'Lapsed';
  if (days === 0) return 'Ends today';
  return `${days} day${days === 1 ? '' : 's'} left`;
};

interface RowProps {
  user: ProAccessUser;
  busy: boolean;
  onAct: (payload: ProAccessAction) => void;
}

const UserRow: React.FC<RowProps> = ({ user, busy, onAct }) => {
  const standing = standingOf(user);
  const style = STANDING[standing];
  const expires = formatDate(user.pro_expires_at);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 md:items-center px-5 py-4 border-t border-ink-50">
      {/* Who */}
      <div className="md:col-span-4 min-w-0">
        <p className="text-sm font-medium text-ink-900 truncate">
          {user.full_name || user.username}
        </p>
        <p className="text-xs text-ink-400 truncate">{user.email || user.username}</p>
      </div>

      {/* Standing */}
      <div className="md:col-span-2">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${style.chip}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {style.label}
        </span>
      </div>

      {/* Time left */}
      <div className="md:col-span-3">
        <p className="text-sm text-ink-800 tabular-nums">{countdown(user)}</p>
        <p className="text-[11px] text-ink-400">
          {expires ? `Until ${expires}` : user.is_pro ? 'No end date' : 'No access'}
          {user.pro_extra_days !== 0 && (
            <span className="ml-1 text-ink-500">
              ({user.pro_extra_days > 0 ? '+' : ''}{user.pro_extra_days}d by admin)
            </span>
          )}
        </p>
      </div>

      {/* Controls */}
      <div className="md:col-span-3 flex flex-wrap items-center gap-1.5 md:justify-end">
        {user.is_pro ? (
          <>
            <button
              type="button" disabled={busy}
              onClick={() => onAct({ action: 'extend', days: 7 })}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40"
            >
              +7d
            </button>
            <button
              type="button" disabled={busy}
              onClick={() => onAct({ action: 'extend', days: 30 })}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40"
            >
              +30d
            </button>
            <button
              type="button" disabled={busy}
              onClick={() => onAct({ action: 'extend', days: -7 })}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium text-ink-600 bg-ink-50 hover:bg-ink-100 disabled:opacity-40"
            >
              -7d
            </button>
            <button
              type="button" disabled={busy}
              title={user.pro_never_expires ? 'Give this pass an end date again' : 'Never expires'}
              onClick={() => onAct({ action: 'set_never_expires', value: !user.pro_never_expires })}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium disabled:opacity-40 ${
                user.pro_never_expires
                  ? 'text-violet-700 bg-violet-100 hover:bg-violet-200'
                  : 'text-ink-600 bg-ink-50 hover:bg-ink-100'
              }`}
            >
              <InfinityIcon className="w-3.5 h-3.5" />
            </button>
            <button
              type="button" disabled={busy}
              onClick={() => onAct({ action: 'revoke' })}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 disabled:opacity-40"
            >
              Revoke
            </button>
          </>
        ) : (
          <button
            type="button" disabled={busy}
            onClick={() => onAct({ action: 'grant' })}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 disabled:opacity-40"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Grant PRO
          </button>
        )}
      </div>
    </div>
  );
};

const ProAccessAdmin: React.FC = () => {
  const [data, setData] = useState<ProAccessUser[]>([]);
  const [accessDays, setAccessDays] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (search?: string) => {
    setIsRefreshing(true);
    try {
      const body = await fetchProAccess(search);
      setData(body.results);
      setAccessDays(body.access_days);
      setError(null);
    } catch {
      setError('Could not load PRO access.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => load(query.trim() || undefined), 300);
    return () => clearTimeout(timer);
  }, [query, load]);

  const act = async (user: ProAccessUser, payload: ProAccessAction) => {
    setBusyId(user.id);
    setNotice(null);
    try {
      const updated = await adjustProAccess(user.id, payload);
      // Patch in place rather than refetching, so the row does not jump to a
      // new sort position while the admin is still working on it.
      setData((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
      const name = updated.full_name || updated.username;
      setNotice(
        payload.action === 'extend'
          ? `${name}: ${payload.days > 0 ? 'added' : 'removed'} ${Math.abs(payload.days)} days`
          : payload.action === 'grant' ? `${name}: PRO granted`
          : payload.action === 'revoke' ? `${name}: PRO revoked`
          : `${name}: ${payload.value ? 'now never expires' : 'expiry restored'}`,
      );
    } catch (err: any) {
      setNotice(err?.response?.data?.error || 'That change did not go through.');
    } finally {
      setBusyId(null);
    }
  };

  const counts = useMemo(() => {
    const out = { unlimited: 0, active: 0, lapsed: 0, never: 0 };
    data.forEach((u) => { out[standingOf(u)] += 1; });
    return out;
  }, [data]);

  // Anyone inside a week of losing access, soonest first - the reason to be here.
  const endingSoon = useMemo(
    () => data.filter((u) => u.is_pro_active && !u.pro_never_expires
      && u.pro_days_remaining !== null && u.pro_days_remaining <= 7).length,
    [data],
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500" />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-[1400px]">
      {/* ---------------- Header ---------------- */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-brand-500" />
            <MicroLabel>Admin - PRO access</MicroLabel>
          </div>
          <h1 className="font-display font-normal tracking-tight text-3xl text-ink-900">
            Subscriber access
          </h1>
          <p className="text-sm text-ink-500 mt-2 font-light">
            {accessDays !== null
              ? `PRO is a one-off purchase giving ${accessDays} days. Extend, shorten or grant access here.`
              : 'Extend, shorten or grant PRO access here.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/dashboard/admin"
            className="flex items-center px-5 py-2.5 rounded-lg text-sm font-medium text-ink-600 bg-ink-50 hover:bg-ink-100 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Analytics
          </Link>
          <button
            type="button"
            onClick={() => load(query.trim() || undefined)}
            disabled={isRefreshing}
            className={`flex items-center px-5 py-2.5 rounded-lg text-sm font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 transition-colors duration-200 ${
              isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ---------------- Summary ---------------- */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {([
          ['active', 'Active passes', counts.active],
          ['unlimited', 'Unlimited', counts.unlimited],
          ['lapsed', 'Lapsed', counts.lapsed],
          ['never', 'Never bought', counts.never],
        ] as const).map(([key, label, value]) => (
          <div key={key} className="bg-surface rounded-xl shadow-sm p-4">
            <MicroLabel>{label}</MicroLabel>
            <p className="text-2xl font-semibold text-ink-900 mt-1.5 tabular-nums leading-none">
              {value}
            </p>
          </div>
        ))}
      </div>

      {endingSoon > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-6">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            {endingSoon} {endingSoon === 1 ? 'pass runs' : 'passes run'} out within 7 days.
            They are at the top of the list.
          </p>
        </div>
      )}

      {notice && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-ink-200 bg-surface px-4 py-3 mb-6">
          <span className="flex items-center gap-2 text-xs text-ink-700">
            <Check className="w-3.5 h-3.5 text-emerald-600" />
            {notice}
          </span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">
            <X className="w-3.5 h-3.5 text-ink-400 hover:text-ink-600" />
          </button>
        </div>
      )}

      {/* ---------------- Table ---------------- */}
      <div className="bg-surface rounded-xl shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 border-b border-ink-100">
          <div>
            <h2 className="font-display font-normal tracking-tight text-lg text-ink-900">Users</h2>
            <p className="text-xs text-ink-400 mt-0.5">
              {data.length} shown, soonest to lapse first
            </p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-300" />
            <input
              type="search"
              aria-label="Search users"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name or email..."
              className="pl-9 pr-3 py-2 w-full sm:w-64 text-sm border border-ink-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-300 transition"
            />
          </div>
        </div>

        {/* Headings - hidden on mobile, where rows stack */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-2.5 bg-ink-50/60">
          <div className="col-span-4"><MicroLabel>User</MicroLabel></div>
          <div className="col-span-2"><MicroLabel>Standing</MicroLabel></div>
          <div className="col-span-3"><MicroLabel>Time left</MicroLabel></div>
          <div className="col-span-3 text-right"><MicroLabel>Adjust</MicroLabel></div>
        </div>

        {error ? (
          <div className="px-5 py-16 text-center">
            <p className="text-sm text-ink-500">{error}</p>
          </div>
        ) : data.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <p className="text-sm text-ink-400">
              {query ? 'No users match that search.' : 'No users yet.'}
            </p>
          </div>
        ) : (
          data.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              busy={busyId === user.id}
              onAct={(payload) => act(user, payload)}
            />
          ))
        )}
      </div>

      <p className="text-[11px] text-ink-400 mt-6">
        Extending adds days on top of the standard window and leaves the purchase
        date alone, so the record of when someone paid stays accurate.
      </p>
    </div>
  );
};

export default ProAccessAdmin;
